import { BffApiClientMolecule } from "@/app/api-client/store"
import {
  TransactionEvent,
  getTransactionErrorMessage,
} from "@/app/sdk/action-molecules/events/transaction-event"
import { EvmSdkContainerMoleculeReatom } from "@/app/sdk/blockchains/evm-sdk-container-molecule.reatom"
import { TransferItemToast } from "@/app/sdk/toasts/transfer-toast"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import type { NftMiniature } from "@anaconda/core/nft/types"
import {
  type Address,
  parseUnionAddress,
  transferErc721FactoryReatom,
} from "@rarible_int/anaconda_sdk/ethereum"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import {
  action,
  atom,
  computed,
  withAsync,
  withCallHook,
  wrap,
} from "@reatom/core"
import { molecule } from "bunshi"
import { getBaseNftEventContext } from "../utils/get-base-nft-event-context"
import { getRoyaltyQueryParams } from "./get-royalty-query-params"
import { getNftActivityQueryParams } from "./nft-activity-query"
import { getNftOffersQueryParams } from "./nft-offers-query"
import { NftScope } from "./scope"
import { type QueryInput, getNftQueryParams } from "./utils"

export type TransferArgs = { to: Address; nft: NftMiniature }

const wait = (ms = 5000) => new Promise((resolve) => setTimeout(resolve, ms))

export const NftMoleculeReatom = molecule((mol, scope) => {
  const { nftId } = scope(NftScope)
  const { sdkContainerAtom } = mol(EvmSdkContainerMoleculeReatom)

  const bffApiClient = mol(BffApiClientMolecule)
  const { address } = mol(WalletConnectMoleculeReatom)
  const faro = mol(LoggerMolecule)

  // Query input atom - simple state holder
  const queryInputAtom = atom<QueryInput>({ id: nftId }, "nftQueryInput")

  // NFT query params - returns TanStack Query params with refetch interval
  const nftQueryParamsAtom = computed(() => {
    const queryInput = queryInputAtom()
    return {
      ...getNftQueryParams(bffApiClient, queryInput),
      refetchInterval: 1000,
      retry: false,
    }
  }, "nftQueryParams")

  // Royalty query params - static, just returns the params
  const nftRoyaltyQueryParamsAtom = computed(
    () => getRoyaltyQueryParams(bffApiClient, nftId),
    "nftRoyaltyQueryParams",
  )

  // Activity query params - returns infinite query params
  const nftActivityQueryParamsAtom = computed(
    () => getNftActivityQueryParams(bffApiClient, { id: nftId }),
    "nftActivityQueryParams",
  )

  const nftOffersQueryParamsAtom = computed(
    () => ({
      ...getNftOffersQueryParams(bffApiClient, { id: nftId }),
      // No refetchInterval: this is an infinite query, so a tick refetches
      // every loaded page, and each page costs the BFF one on-chain read per
      // offer. Offers are refreshed on focus and after our own mutations.
      refetchOnWindowFocus: true,
    }),
    "nftOffersQueryParams",
  )

  // Reset metadata mutation action
  const resetMetaAction = action(async () => {
    const queryInput = queryInputAtom()

    // Call BFF API to reset metadata
    const response = await wrap(
      bffApiClient.api.nfts.resetMeta.$post({
        json: queryInput,
      }),
    )

    if (!response.ok) {
      throw new Error("Failed to reset metadata")
    }

    // Wait for metadata to update
    await wrap(wait())

    return { queryInput, timestamp: Date.now() }
  }, "resetMeta").extend(withAsync())

  // Transfer NFT mutation action
  const transferNftAction = action(async (args: TransferArgs) => {
    const { to, nft } = args
    const walletAddress = address()
    const sdk = sdkContainerAtom()

    // Validation
    if (!sdk) {
      throw new Error("EVM SDK is not initialized for transfer NFT")
    }
    if (!walletAddress) {
      throw new Error("Address is not defined for transfer NFT")
    }

    // Parse NFT ID
    const itemId = nft.id
    const { address: collectionId, tokenId } = parseUnionAddress(itemId)
    if (tokenId === undefined) {
      throw new Error("Token ID is not defined for transfer NFT")
    }

    // Execute transfer using the Reatom SDK
    const transferAction = transferErc721FactoryReatom(sdk)

    // Execute the transfer with our parameters
    const result = await wrap(
      transferAction({
        from: walletAddress,
        to,
        collectionId,
        tokenId,
      }),
    )

    return result.txId
  }, "transferNft").extend(
    withAsync(),

    // onCall: Called when action starts (analytics)
    withCallHook(async (values, [args]) => {
      const { to, nft } = args
      const baseTransactionEventContext: EventContext = {
        flow: Flow.TransferItem,
        to,
        ...getBaseNftEventContext(nft),
      }
      faro?.api.pushEvent(TransactionEvent.Started, baseTransactionEventContext)
      amplitude.track(TransactionEvent.Started, baseTransactionEventContext)
    }),
  )

  // Success hook for transfer
  transferNftAction.onFulfill.extend(
    withCallHook((result) => {
      const args = result.params[0]
      const { to, nft } = args
      const context: EventContext = {
        flow: Flow.TransferItem,
        to,
        ...getBaseNftEventContext(nft),
      }
      faro?.api.pushEvent(TransactionEvent.Completed, context)
      amplitude.track(TransactionEvent.Completed, context)

      // Show success toast
      sonnerToast.dismiss()
      sonnerToast.custom((id) => <TransferItemToast.Success id={id} />, {
        duration: Number.POSITIVE_INFINITY,
      })
    }),
  )

  // Error hook for transfer
  transferNftAction.onReject.extend(
    withCallHook(
      ({ error, params }: { error: unknown; params: [TransferArgs] }) => {
        const { to, nft } = params[0]
        console.error("Error during NFT transfer:", error)
        const context = {
          flow: Flow.TransferItem,
          to,
          ...getBaseNftEventContext(nft),
          error: getTransactionErrorMessage(error),
        }
        faro?.api.pushEvent(TransactionEvent.Failed, context)
        amplitude.track(TransactionEvent.Failed, context)

        // Show error toast
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <TransferItemToast.Error nft={nft} id={id} error={error} />
        ))
      },
    ),
  )

  return {
    nftQueryParamsAtom,
    nftRoyaltyQueryParamsAtom,
    nftActivityQueryParamsAtom,
    nftOffersQueryParamsAtom,
    resetMetaAction,
    transferNftAction,
    queryInputAtom,
  }
})
